import { generateGuid } from "@amx/ux-twoui-utils";
import React, { useContext, useReducer } from "react";
import { NotificationBarContainerHost } from "../NotificationBar";

export interface NotificationBarProviderContextType {
    readonly showNotificationBar: (notificationBar: React.ReactElement) => void;
    readonly removeNotificationBar: (id: string) => void;
}

interface ControlledNotificationBar {
    readonly id: string;
    readonly notificationBar: React.ReactElement;
}

interface ControlledNotificationOpenAction {
    readonly type: "openNotificationBar";
    readonly notificationBar: React.ReactElement;
    readonly id: string;
}

interface ControlledNotificationCloseAction {
    readonly type: "closeNotificationBar";
    readonly id: string;
}

interface NotificationBarIdProviderProps {
    readonly id: string;
}

type ControlledNotificationBarAction = ControlledNotificationOpenAction | ControlledNotificationCloseAction;

export const NotificationBarProviderContext = React.createContext<NotificationBarProviderContextType | undefined>(undefined);

const notificationBarReducer = (controlledNotificationBars: readonly ControlledNotificationBar[], action: ControlledNotificationBarAction) => {
    switch (action.type) {
        case "closeNotificationBar":
            return controlledNotificationBars.filter((controlledNotificationBar: ControlledNotificationBar) => {
                return controlledNotificationBar.id !== action.id;
            });
        case "openNotificationBar":
            if (controlledNotificationBars.length === 0) {
                return [{
                    id: action.id,
                    notificationBar: action.notificationBar
                }];
            } else {
                return [
                    {
                        id: action.id,
                        notificationBar: action.notificationBar
                    },
                    controlledNotificationBars[0]
                ];
            }
        default:
            return controlledNotificationBars;
    }
};

export const NotificationBarProvider: React.FC = ({ children }) => {

    const [controlledNotificationBars, dispatch] = useReducer(notificationBarReducer, []);

    const showNotificationBar = (notificationBar: React.ReactElement) => {
        const newId = generateGuid();

        dispatch({
            type: "openNotificationBar",
            notificationBar,
            id: newId
        });
    };

    const removeNotificationBar = (id: string) => {
        dispatch({
            type: "closeNotificationBar",
            id
        });
    };

    const notificationBarsRendered = controlledNotificationBars.map((notificationBar: ControlledNotificationBar) => {
        return (
            <NotificationBarIdProvider
                id={notificationBar.id}
                key={notificationBar.id}
            >
                {notificationBar.notificationBar}
            </NotificationBarIdProvider>
        );
    });

    return (
        <NotificationBarProviderContext.Provider
            value={{
                showNotificationBar,
                removeNotificationBar
            }}
        >
            {children}
            <NotificationBarContainerHost>
                {notificationBarsRendered}
            </NotificationBarContainerHost>
        </NotificationBarProviderContext.Provider>
    );
};

export const useNotificationBarController = () => {
    return useContext(NotificationBarProviderContext);
};

export const NotificationBarIdProviderContext = React.createContext<string>("");

export const useNotificationBarId = () => {
    return useContext(NotificationBarIdProviderContext);
};

export const NotificationBarIdProvider: React.FC<NotificationBarIdProviderProps> = ({
    children,
    id
}) => {
    return (
        <NotificationBarIdProviderContext.Provider value={id}>
            {children}
        </NotificationBarIdProviderContext.Provider>
    );
};
